/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import { useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Provider } from 'react-redux'
import { persistStore } from 'redux-persist'
import { Router, Route } from 'react-router'
import { go, routes } from './location'

import { beginGame, endGame } from './actions/game'
import AboutScreen from './containers/AboutScreen'
import TitleScreen from './containers/TitleScreen'
import GameScreen from './containers/GameScreen'
import SettingsScreen from './containers/SettingsScreen'
import ScoresScreen from './containers/ScoresScreen'
import { LEVELS, log } from './logs'

let timeoutId = null

/**
 * Component to Delay Render Until Rehydration Complete
 */
const AppProvider = props => {
  log('AppProvider', LEVELS.INFO, props)
  const intl = useIntl()
  const { store, history } = props
  const [rehydrated, setRehydrated] = useState(false)
  const willMount = useRef(true)
  if (willMount.current) {
    persistStore(
      store,
      {
        blacklist: ['game', 'routing', 'decks'],
        keyPrefix: 'memokid2'
      },
      () => {
        log('AppProvider', LEVELS.DEBUG, 'rehydration complete')
        setRehydrated(true)
      }
    )
  }
  willMount.current = false

  if (!rehydrated) {
    return <div>{intl.formatMessage({ id: 'common.loading' })}</div>
  }
  return (
    <Provider store={store}>
      <Router history={history}>
        <Route
          path={routes.HOME}
          component={TitleScreen}
          onEnter={() => {
            log('AppProvider', LEVELS.DEBUG, 'title enter')
            timeoutId = setTimeout(() => {
              log('AppProvider', LEVELS.DEBUG, 'demo start')
              store.dispatch(go(routes.DEMO))
            }, 20000)
          }}
          onLeave={() => {
            log('AppProvider', LEVELS.DEBUG, 'title leave')
            clearTimeout(timeoutId)
          }}
        />
        <Route path={routes.ABOUT} component={AboutScreen} />
        <Route
          path={routes.GAME} component={GameScreen}
          onEnter={() => store.dispatch(beginGame())}
          onLeave={() => store.dispatch(endGame())}
        />
        <Route
          path={routes.DEMO} component={GameScreen}
          onEnter={() => store.dispatch(beginGame(true))}
          onLeave={() => store.dispatch(endGame())}
        />
        <Route path={routes.SETTINGS} component={SettingsScreen} />
        <Route path={routes.SCORES} component={ScoresScreen} />
      </Router>
    </Provider>
  )
}

AppProvider.displayName = 'AppProvider'

export default AppProvider
